import { useQuery } from "@apollo/client/react";
import { Plus, SearchX } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import useChatDocuments from "../../../../../hooks/agent/useChatDocuments";
import ModalProvider from "../../../../../globalProviders/modal";
import { Box } from "../../../../../ui/box";
import { Button, ButtonIcon, ButtonText } from "../../../../../ui/button";
import { HStack } from "../../../../../ui/hstack";
import { Icon } from "../../../../../ui/icon";
import { Spinner } from "../../../../../ui/spinner";
import { Text } from "../../../../../ui/text";
import { VStack } from "../../../../../ui/vstack";
import { formatMoneyFromFragment } from "../../../../../utils/money";
import CreateProduct from "../../../../product/create";
import Search from "../../../../search";
import type { SelectOption } from "../../../wire";
import OptionCard from "./OptionCard";

/** How many products a page pulls. Small on purpose — this list is meant to be
 * scanned a screenful at a time, not scrolled through in one go. */
const PAGE_SIZE = 20;

/** How long the search box waits after the last keystroke before it becomes a
 * network request. The catalogue is never loaded client-side (a brand can have
 * thousands of products), so every keystroke that fires early is a wasted
 * round trip against a list the user hasn't finished typing yet. */
const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  brandId: string;
  /** A card was pressed, or a product was just created from the modal. */
  onPick: (option: SelectOption) => void;
  disabled: boolean;
};

/**
 * A brand's catalogue as a searchable, paginated list of cards — and nothing else.
 *
 * It owns the search box, the query, the pagination and the "Crear producto" modal, and
 * reports which product was chosen, whether pressed or just created. What is done with
 * it is the caller's.
 *
 * The catalogue is never loaded client-side: a brand can have thousands of products, so
 * the list is fetched a page at a time, keyed on the search term.
 */
const ProductCatalog = ({ brandId, onPick, disabled }: Props) => {
  const documents = useChatDocuments();
  const [search, setSearch] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, loading, fetchMore } = useQuery(documents.productsSelection, {
    variables: { brand_Id: brandId, first: PAGE_SIZE, search },
  });
  // Needed only to seed the "Crea tu producto" modal's price field with the
  // brand's own currency — this list has no app-level brand context to read it
  // from (it's shared across apps), so it fetches it directly.
  const { data: brandData } = useQuery(documents.brand, {
    variables: { id: brandId },
  });
  const brandCurrency = brandData?.brand.get?.country?.currencyCode ?? null;

  const options: SelectOption[] = useMemo(
    () =>
      (data?.product.list.edges ?? []).map(({ node }) => ({
        value: node.id,
        label: node.name,
        description: node.description,
        imageUrl: node.image?.url ?? "",
        badge: formatMoneyFromFragment(node.price),
      })),
    [data],
  );
  const pageInfo = data?.product.list.pageInfo;

  /**
   * Open the same "Crea tu producto" modal the manual campaign wizard uses.
   *
   * A brand whose catalogue is empty (or simply missing the thing this campaign is
   * about) would otherwise have no way forward from inside the picker. Creating it here
   * puts a real row in the catalogue, and hands it to the caller as the pick.
   */
  const openCreateProduct = useCallback(() => {
    if (!brandCurrency) return;
    ModalProvider.show({
      children: (
        <CreateProduct
          brandId={brandId}
          currency={brandCurrency}
          // The modal renders in the app-root slot, outside the chat's guest scope,
          // so it is handed the mutation to run rather than looking it up.
          mutation={documents.createProduct}
          mutationOptions={{
            onCompleted: (productData) => {
              const product = productData.product.create.product;
              if (!product) return;
              onPick({
                value: product.id,
                label: product.name,
                description: product.description,
                imageUrl: product.image?.url ?? "",
                badge: formatMoneyFromFragment(product.price),
              });
            },
            // The list this renders is a paginated query of the very catalogue that
            // just changed; without this the new product is selected but invisible
            // in the options behind it.
            refetchQueries: [documents.productsSelection],
            awaitRefetchQueries: true,
          }}
        />
      ),
    });
  }, [brandId, brandCurrency, onPick, documents]);

  const loadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMore) return;
    setLoadingMore(true);
    await fetchMore({ variables: { after: pageInfo.endCursor } });
    setLoadingMore(false);
  };

  return (
    <VStack space="sm">
      <HStack space="sm" className="items-center">
        <Box className="flex-1">
          <Search
            size="sm"
            placeholder="Buscar productos…"
            onChange={setSearch}
            debounceTime={SEARCH_DEBOUNCE_MS}
            inputProps={{ isDisabled: disabled }}
          />
        </Box>
        <Button
          action="brand"
          variant="outline"
          size="sm"
          rounded="lg"
          isDisabled={disabled || !brandCurrency}
          onPress={openCreateProduct}
        >
          <ButtonIcon as={Plus} size="sm" />
          <ButtonText>Crear producto</ButtonText>
        </Button>
      </HStack>

      <Box className="w-full max-h-96">
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerClassName="w-full"
        >
          {loading && !data ? (
            <Box className="items-center py-8">
              <Spinner size="small" />
            </Box>
          ) : options.length ? (
            <VStack space="xs">
              {options.map((option) => (
                <OptionCard
                  key={option.value}
                  option={option}
                  selected={false}
                  disabled={disabled}
                  onPress={() => onPick(option)}
                />
              ))}
              {pageInfo?.hasNextPage ? (
                <Button
                  action="muted"
                  variant="link"
                  size="xs"
                  isDisabled={loadingMore || disabled}
                  onPress={() => void loadMore()}
                >
                  <ButtonText>
                    {loadingMore ? "Cargando…" : "Ver más"}
                  </ButtonText>
                </Button>
              ) : null}
            </VStack>
          ) : (
            // Two ways out of an empty catalogue: create the product here, or paste
            // its link below the list.
            <VStack space="xs" className="items-center py-8">
              <Icon as={SearchX} size="md" className="text-typography-300" />
              <Text className="text-center text-sm text-typography-500">
                {search
                  ? `No encontramos productos para “${search}”.`
                  : "Tu marca aún no tiene productos cargados."}{" "}
                Créalo acá, o pega el link del producto.
              </Text>
              <Button
                action="brand"
                variant="solid"
                size="sm"
                rounded="lg"
                className="mt-2"
                isDisabled={disabled || !brandCurrency}
                onPress={openCreateProduct}
              >
                <ButtonIcon as={Plus} size="sm" />
                <ButtonText>Crear producto</ButtonText>
              </Button>
            </VStack>
          )}
        </ScrollView>
      </Box>
    </VStack>
  );
};

export default ProductCatalog;
